import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Reveal } from '../components/motion.tsx';
import { Button, Callout, PageHead, Panel, Skeleton, Switch, Tabs, useToast } from '../components/ui.tsx';
import { ApiError, get, put } from '../lib/api.ts';

type Setting = { value: unknown; label: string; legacy: string; min?: number; max?: number; default: unknown };
type All = Record<'attendance' | 'payroll' | 'canteen', Record<string, Setting>>;

const PARITY_COPY = {
  payroll: {
    legacy: { title: 'Match the old system exactly', body: 'Every known defect reproduced behind named switches, so migrated periods recompute to the rupiah. Use for parallel runs.' },
    corrected: { title: 'Corrected rules', body: 'PPh 21 brackets use (max − min); prorate variables unswapped; THR respects inactive rules; formula errors surface instead of silently becoming 0.' },
  },
  attendance: {
    legacy: { title: 'Match the old system exactly', body: 'Overnight early-outs ignored; only unpaid permits excuse an absence.' },
    corrected: { title: 'Corrected rules', body: 'Overnight shifts measured properly; any approved permit excuses an absence.' },
  },
} as const;

export function Settings() {
  const q = useQuery({ queryKey: ['settings'], queryFn: () => get<All>('/api/settings') });
  const [tab, setTab] = useState<'payroll' | 'attendance' | 'canteen'>('payroll');
  return (
    <div>
      <PageHead
        eyebrow="Settings"
        title={
          <>
            How Hearth <em>behaves.</em>
          </>
        }
        lede="These replace the legacy attendanceSetting, salaryPayrollSetting, canteenSettings and IDIndexes tables. Each shows the old key it migrated from."
      />
      <Tabs idPrefix="settings" value={tab} onChange={setTab} tabs={[{ id: 'payroll', label: 'Payroll' }, { id: 'attendance', label: 'Attendance' }, { id: 'canteen', label: 'Canteen' }]} />
      <div style={{ marginTop: 'var(--s-6)' }}>{q.isLoading ? <Skeleton h={400} /> : q.data ? <Namespace ns={tab} settings={q.data[tab]} /> : null}</div>
    </div>
  );
}

function Namespace({ ns, settings }: { ns: 'payroll' | 'attendance' | 'canteen'; settings: Record<string, Setting> }) {
  const parityKey = ns === 'payroll' ? 'parity' : ns === 'attendance' ? 'metricsParity' : null;
  return (
    <Reveal className="grid-12">
      {parityKey && ns !== 'canteen' ? (
        <Reveal.Item className="col-12">
          <Panel eyebrow="Calculation parity" title={<>Which <em className="serif-italic">rules?</em></>}>
            <ParityPicker ns={ns} value={String(settings[parityKey]?.value ?? 'legacy')} settingKey={parityKey} />
          </Panel>
        </Reveal.Item>
      ) : null}
      <Reveal.Item className="col-12">
        <Panel eyebrow={ns} title="Values">
          {Object.entries(settings)
            .filter(([k]) => k !== parityKey)
            .map(([k, s]) => (
              <SettingRow key={k} ns={ns} k={k} s={s} />
            ))}
        </Panel>
      </Reveal.Item>
    </Reveal>
  );
}

function ParityPicker({ ns, value, settingKey }: { ns: 'payroll' | 'attendance'; value: string; settingKey: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (v: string) => put(`/api/settings/${ns}/${settingKey}`, { value: v }),
    onSuccess: () => {
      toast({ title: 'Parity mode changed', body: 'Applies from the next calculation. Existing runs keep the mode they were calculated with.' });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  return (
    <div className="stack">
      <div className="parity">
        {(['legacy', 'corrected'] as const).map((opt) => (
          <button key={opt} type="button" className="parity__option" aria-pressed={value === opt} onClick={() => m.mutate(opt)} disabled={m.isPending}>
            <span className="eyebrow">{opt}</span>
            <span className="display" style={{ fontSize: '1.4rem' }}>
              {PARITY_COPY[ns][opt].title}
            </span>
            <span className="muted" style={{ fontSize: 'var(--step--1)' }}>
              {PARITY_COPY[ns][opt].body}
            </span>
          </button>
        ))}
      </div>
      {ns === 'payroll' && value === 'legacy' ? (
        <Callout title="Recommended during migration.">Run legacy and Hearth side by side for at least two periods; switch to corrected rules only after HR and your tax consultant sign off on each difference.</Callout>
      ) : null}
    </div>
  );
}

function SettingRow({ ns, k, s }: { ns: string; k: string; s: Setting }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<unknown>(s.value);
  useEffect(() => setV(s.value), [s.value]);
  const m = useMutation({
    mutationFn: (value: unknown) => put(`/api/settings/${ns}/${k}`, { value }),
    onSuccess: () => {
      toast({ title: 'Saved', body: s.label });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => toast({ title: 'Not saved', body: e instanceof ApiError ? e.message : undefined, tone: 'error' }),
  });
  const dirty = JSON.stringify(v) !== JSON.stringify(s.value);
  const isBool = typeof s.default === 'boolean';
  const isNum = typeof s.default === 'number';
  const options = k === 'taxMethod' ? ['annualised', 'ter'] : k === 'leaveDayCounting' ? ['calendar', 'working'] : null;
  return (
    <div className="setting">
      <div>
        <div>{s.label}</div>
        {s.legacy ? <div className="setting__legacy">legacy: {s.legacy}</div> : null}
        {k === 'taxMethod' && v === 'ter' ? (
          <div style={{ marginTop: 8 }}>
            <Callout>TER needs the PP 58/2023 rate tables loaded first; calculation stops with a clear error until they are.</Callout>
          </div>
        ) : null}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {isBool ? (
          <Switch checked={Boolean(v)} onChange={(x) => m.mutate(x)} label={s.label} />
        ) : options ? (
          <select className="select" value={String(v)} onChange={(e) => m.mutate(e.target.value)}>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <>
            <input className="input input--mono" style={{ maxWidth: 180 }} type={isNum ? 'number' : 'text'} min={s.min} max={s.max} value={String(v ?? '')} onChange={(e) => setV(isNum ? Number(e.target.value) : e.target.value)} aria-label={s.label} />
            {dirty ? (
              <Button size="sm" variant="primary" loading={m.isPending} onClick={() => m.mutate(v)}>
                Save
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
